import { Box, Button, Card, CardContent, Chip, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import MedicationIcon from '@mui/icons-material/Medication';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { EmptyState } from '@/components/EmptyState';
import { useApiQuery } from '@/hooks/useApiQuery';
import { clinician } from '@/api/endpoints';
import { formatDate, formatDateTime } from '@/utils/format';
import type { Prescription, PrescriptionStatus } from '@/types/medical';

const ACTIVE: PrescriptionStatus[] = ['draft', 'signed', 'registered', 'partly_dispensed'];
const HISTORY: PrescriptionStatus[] = ['dispensed', 'expired', 'cancelled', 'failed'];

type TabKey = 'active' | 'history';

export function ClinicianPrescriptionsPage() {
  const [params] = useSearchParams();
  const appointmentParam = params.get('appointment') ?? undefined;

  const q = useApiQuery({
    queryKey: ['clinician', 'prescriptions'],
    queryFn:  clinician.prescriptions.list,
  });

  const buckets = useMemo(() => {
    const all = q.data ?? [];
    return {
      active:  all.filter((r) => ACTIVE.includes(r.status)),
      history: all.filter((r) => HISTORY.includes(r.status)),
    };
  }, [q.data]);

  const [tab, setTab] = useState<TabKey>('active');

  return (
    <>
      <PageHeader
        title="Prescriptions"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            component={RouterLink}
            to={`/clinician/prescriptions/new${appointmentParam ? `?appointment=${appointmentParam}` : ''}`}
          >
            New prescription
          </Button>
        }
      />
      <DataState query={q}>
        {() => (
          <>
            <Tabs
              value={tab} onChange={(_, v: TabKey) => setTab(v)}
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab value="active"  label={`Active (${buckets.active.length})`} />
              <Tab value="history" label={`History (${buckets.history.length})`} />
            </Tabs>
            {buckets[tab].length === 0 ? (
              <EmptyState
                title={tab === 'active' ? 'No active prescriptions' : 'No history yet'}
                description={tab === 'active'
                  ? 'Drafts in progress and signed prescriptions appear here.'
                  : undefined}
              />
            ) : (
              <Stack spacing={1.5}>
                {buckets[tab].map((r) => <Row key={r.id} rx={r} />)}
              </Stack>
            )}
          </>
        )}
      </DataState>
    </>
  );
}

function Row({ rx }: { rx: Prescription }) {
  const items = rx.items ?? [];
  const head = items[0];
  return (
    <Card
      component={RouterLink}
      to={`/clinician/prescriptions/${rx.id}`}
      sx={{ textDecoration: 'none', color: 'inherit',
            '&:hover': { borderColor: 'primary.main' } }}
    >
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <MedicationIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {head?.drug_name_snapshot ?? 'Prescription'}
                {items.length > 1 && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {' '}+ {items.length - 1} more
                  </Typography>
                )}
              </Typography>
              <StatusChip status={rx.status} />
            </Stack>
            <Typography variant="body2" color="text.secondary" noWrap>
              Patient #{rx.patient} · {rx.jurisdiction_code}
              {rx.issued_at && ` · ${formatDateTime(rx.issued_at)}`}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right', minWidth: 120 }}>
            <Typography variant="caption" color="text.secondary" className="tabular">
              {rx.valid_until ? `until ${formatDate(rx.valid_until)}` : '—'}
            </Typography>
          </Box>
          <ChevronRightIcon color="action" />
        </Stack>
      </CardContent>
    </Card>
  );
}

function StatusChip({ status }: { status: PrescriptionStatus }) {
  const m: Record<PrescriptionStatus, { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' }> = {
    draft:           { label: 'draft', color: 'warning' },
    signed:          { label: 'signed', color: 'primary' },
    registered:      { label: 'registered', color: 'primary' },
    partly_dispensed:{ label: 'partly filled', color: 'primary' },
    dispensed:       { label: 'dispensed', color: 'success' },
    cancelled:       { label: 'cancelled', color: 'default' },
    expired:         { label: 'expired', color: 'default' },
    failed:          { label: 'failed', color: 'error' },
  };
  const c = m[status];
  return <Chip size="small" label={c.label} color={c.color} variant="outlined" />;
}
